import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { TrainerMemberService } from '../../../../lib/trainerMemberService';
import { WorkoutService } from '../../../../lib/workoutService';
import { sendConnectionInvite } from '../../../../lib/inviteEmailService';
import { shouldAttemptInviteEmail } from '../../../../lib/memberInviteLogic';
import { getRosterViewMode } from '../../../../lib/memberRosterViewMode';
import { TrainerMemberRelationshipWithMember } from '../../../../types/dataModel';
import { AddMemberModalLayer } from '../../../../components/AddMemberModalLayer';
import { addMemberModalOpen } from '../../../../lib/addMemberModalStore';
import { displayNameFromProfile } from '../../../../pages/account/accountShared';
import { matchesMemberSearch } from '../../../../lib/memberManagementFilters';
import { MoreVertical, Plus, Search, X } from '../../bravosIcons';
import { MembersEmptyHero } from './memberManagement/MembersEmptyHero';
import {
  localCalendarDateKey,
  paddedWideRangeForMemberCardSessions,
  planAssignmentIsFromTrainer,
  type SessionLike,
} from '../../../../lib/memberLast7DaysActivity';
import {
  CADENCE_TOOLTIP,
  STALE_INVITE_TOOLTIP,
  STRIP_BAR_MEANING,
  STRIP_WEEK_LABELS,
  computeMemberRosterMetrics,
  formatCadenceDisplay,
  formatInviteAbsoluteDate,
  initialsFromNameOrEmail,
  isInviteStale,
  joinDateYmd,
  pad2,
  type MemberRosterMetrics,
  type StripBarClass,
} from '../../../../lib/memberCadenceMetrics';
import './MembersManagement.css';

interface MemberManagementProps {
  trainerId: string;
  trainerName?: string;
  pendingOpenAddMember?: boolean;
  onPendingOpenAddMemberConsumed?: () => void;
}

type SortKey = 'name' | 'cadence';
type SortDir = 'asc' | 'desc';

type PendingRow =
  | { kind: 'relationship'; relationship: TrainerMemberRelationshipWithMember }
  | { kind: 'email'; email: string; createdAt: string };

function ConsistencyStrip({ strip }: { strip: MemberRosterMetrics['strip'] }) {
  return (
    <div className="strip" aria-label="8-week consistency">
      {strip.map((w, i) => {
        const bar = w.bar as StripBarClass;
        const title = `${STRIP_WEEK_LABELS[i] || ''}: ${STRIP_BAR_MEANING[bar] || ''}`;
        return <span key={w.weekStart} className={`bar ${bar}`} title={title} />;
      })}
    </div>
  );
}

function emptyMetrics(): MemberRosterMetrics {
  return {
    strip: Array.from({ length: 8 }, (_, i) => ({
      weekStart: `empty-${i}`,
      planned: 0,
      completed: 0,
      bar: 'miss' as const,
    })),
    cadence: 0,
    last: { label: '—', warn: false, daysAgo: null },
    next: { label: 'Not scheduled', kind: 'empty', nextYmd: null },
    trainingToday: false,
    needsAttention: false,
    needsAttentionTooltip: null,
  };
}

export const MemberManagement: React.FC<MemberManagementProps> = ({
  trainerId,
  trainerName,
  pendingOpenAddMember,
  onPendingOpenAddMemberConsumed,
}) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<TrainerMemberRelationshipWithMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<{ email: string; createdAt: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTrainingToday, setFilterTrainingToday] = useState(false);
  const [filterNeedsAttention, setFilterNeedsAttention] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('cadence');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [pendingOpen, setPendingOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [metricsByMemberId, setMetricsByMemberId] = useState<Record<string, MemberRosterMetrics>>({});
  const [flashPendingKey, setFlashPendingKey] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [resendingKey, setResendingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState<{
    relationshipId: string;
    memberName: string;
    activePlansCount: number;
  } | null>(null);
  const [menu, setMenu] = useState<{
    relationship: TrainerMemberRelationshipWithMember;
    top: number;
    left: number;
    arrowLeft: number;
    arrowOnTop: boolean;
  } | null>(null);
  const metricsGenRef = useRef(0);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null);
  const pendingSectionRef = useRef<HTMLElement | null>(null);

  const todayYmd = useMemo(() => localCalendarDateKey(new Date()), []);

  const loadMembers = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      try {
        if (!silent) setLoading(true);
        setError(null);
        const [data, invites] = await Promise.all([
          TrainerMemberService.getTrainerMembers(trainerId),
          TrainerMemberService.getPendingInvites(trainerId),
        ]);
        setMembers(data);
        setPendingInvites(invites);
      } catch (err: any) {
        console.error('Error loading members:', err);
        setError(err.message || 'Failed to load members');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [trainerId]
  );

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (members.length === 0) {
      metricsGenRef.current += 1;
      setMetricsByMemberId({});
      return;
    }
    const loadGen = ++metricsGenRef.current;
    const { fromStr, toStr } = paddedWideRangeForMemberCardSessions();
    let cancelled = false;

    const run = async () => {
      const next: Record<string, MemberRosterMetrics> = {};
      await Promise.all(
        members.map(async (m) => {
          if (cancelled) return;
          try {
            const sessions = (await WorkoutService.getMemberWorkoutSessionsLight(
              m.memberId,
              fromStr,
              toStr,
              trainerId
            )) as SessionLike[];
            next[m.memberId] = computeMemberRosterMetrics(
              sessions ?? [],
              trainerId,
              joinDateYmd(m),
              { todayYmd }
            );
          } catch {
            next[m.memberId] = emptyMetrics();
          }
        })
      );
      if (!cancelled && loadGen === metricsGenRef.current) {
        setMetricsByMemberId(next);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [members, trainerId, todayYmd]);

  useEffect(() => {
    if (!pendingOpenAddMember) return;
    addMemberModalOpen();
    onPendingOpenAddMemberConsumed?.();
  }, [pendingOpenAddMember, onPendingOpenAddMemberConsumed]);

  useEffect(() => {
    if (!menu) return;
    const close = () => {
      setMenu(null);
      menuBtnRef.current = null;
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuBtnRef.current?.contains(t)) return;
      const portal = document.getElementById('mm-row-menu');
      if (portal?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menu]);

  const activeMembers = useMemo(() => members.filter((m) => m.status === 'active'), [members]);
  const pendingRelationships = useMemo(
    () => members.filter((m) => m.status === 'pending' || m.status === 'onboarding'),
    [members]
  );
  const archivedMembers = useMemo(() => members.filter((m) => m.status === 'inactive'), [members]);

  const pendingRowsAll: PendingRow[] = useMemo(() => {
    const rels: PendingRow[] = pendingRelationships.map((r) => ({ kind: 'relationship', relationship: r }));
    const emails: PendingRow[] = pendingInvites.map((i) => ({
      kind: 'email',
      email: i.email,
      createdAt: i.createdAt,
    }));
    return [...rels, ...emails];
  }, [pendingRelationships, pendingInvites]);

  const search = searchQuery.trim().toLowerCase();

  const activeFiltered = useMemo(() => {
    let list = activeMembers.filter((m) => matchesMemberSearch(m, searchQuery));
    if (filterTrainingToday || filterNeedsAttention) {
      list = list.filter((m) => {
        const met = metricsByMemberId[m.memberId];
        const tt = met?.trainingToday === true;
        const na = met?.needsAttention === true;
        if (filterTrainingToday && filterNeedsAttention) return tt || na;
        if (filterTrainingToday) return tt;
        return na;
      });
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortKey === 'name') {
        const na = displayNameFromProfile(a.member).toLowerCase();
        const nb = displayNameFromProfile(b.member).toLowerCase();
        const c = na.localeCompare(nb);
        return sortDir === 'asc' ? c : -c;
      }
      const ca = metricsByMemberId[a.memberId]?.cadence ?? 0;
      const cb = metricsByMemberId[b.memberId]?.cadence ?? 0;
      if (ca < cb) return sortDir === 'asc' ? -1 : 1;
      if (ca > cb) return sortDir === 'asc' ? 1 : -1;
      return displayNameFromProfile(a.member)
        .toLowerCase()
        .localeCompare(displayNameFromProfile(b.member).toLowerCase());
    });
    return sorted;
  }, [
    activeMembers,
    searchQuery,
    filterTrainingToday,
    filterNeedsAttention,
    metricsByMemberId,
    sortKey,
    sortDir,
  ]);

  const pendingFiltered = useMemo(() => {
    if (!search) return pendingRowsAll;
    return pendingRowsAll.filter((row) => {
      if (row.kind === 'email') return row.email.toLowerCase().includes(search);
      return matchesMemberSearch(row.relationship, searchQuery);
    });
  }, [pendingRowsAll, search, searchQuery]);

  const archivedFiltered = useMemo(() => {
    return archivedMembers.filter((m) => matchesMemberSearch(m, searchQuery));
  }, [archivedMembers, searchQuery]);

  useEffect(() => {
    if (!search) return;
    if (pendingFiltered.length > 0) setPendingOpen(true);
    if (archivedFiltered.length > 0) setArchivedOpen(true);
  }, [search, pendingFiltered.length, archivedFiltered.length]);

  const trainingTodayCount = useMemo(
    () => activeMembers.filter((m) => metricsByMemberId[m.memberId]?.trainingToday).length,
    [activeMembers, metricsByMemberId]
  );
  const needsAttentionCount = useMemo(
    () => activeMembers.filter((m) => metricsByMemberId[m.memberId]?.needsAttention).length,
    [activeMembers, metricsByMemberId]
  );

  const totalRoster =
    activeMembers.length + pendingRowsAll.length + archivedMembers.length;

  const rosterMode = useMemo(
    () =>
      getRosterViewMode(activeMembers.length, pendingRowsAll.length, archivedMembers.length),
    [activeMembers.length, pendingRowsAll.length, archivedMembers.length]
  );

  // Pending-only: open Pending/Archived so columns are visible without an extra click.
  useEffect(() => {
    if (rosterMode !== 'active-empty') return;
    setPendingOpen(true);
    if (archivedMembers.length > 0) setArchivedOpen(true);
  }, [rosterMode, archivedMembers.length]);

  const viewPendingSection = useCallback(() => {
    setPendingOpen(true);
    window.requestAnimationFrame(() => {
      pendingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const showToast = (kind: 'ok' | 'error', text: string) => {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 5000);
  };

  const openProfile = (relationship: TrainerMemberRelationshipWithMember) => {
    navigate(`/trainer-dashboard/member/${relationship.memberId}`, {
      state: { memberRelationship: relationship },
    });
  };

  const openRowMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    relationship: TrainerMemberRelationshipWithMember
  ) => {
    e.stopPropagation();
    const btn = e.currentTarget;
    if (menu?.relationship.id === relationship.id) {
      setMenu(null);
      menuBtnRef.current = null;
      return;
    }
    menuBtnRef.current = btn;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 200;
    let left = rect.right - menuWidth;
    let top = rect.bottom + 10;
    let arrowOnTop = true;
    if (top + 140 > window.innerHeight) {
      top = rect.top - 140 - 10;
      arrowOnTop = false;
    }
    left = Math.max(8, left);
    const arrowLeft = rect.left + rect.width / 2 - left - 7;
    setMenu({ relationship, top, left, arrowLeft, arrowOnTop });
  };

  const handleArchiveClick = async (relationship: TrainerMemberRelationshipWithMember) => {
    setMenu(null);
    const memberName =
      displayNameFromProfile(relationship.member) || relationship.member?.email || 'This member';
    try {
      const plans = await WorkoutService.getMemberWorkoutPlans(relationship.memberId);
      const activePlans = (plans || []).filter(
        (p) => p.status === 'active' && planAssignmentIsFromTrainer(p, trainerId)
      );
      setArchiveConfirm({
        relationshipId: relationship.id,
        memberName,
        activePlansCount: activePlans.length,
      });
    } catch (err: any) {
      // Still confirm archive if plan lookup fails — avoid silent archive on mis-click.
      setArchiveConfirm({
        relationshipId: relationship.id,
        memberName,
        activePlansCount: 0,
      });
      setError(err.message || 'Failed to check workout plans');
    }
  };

  const doArchive = async (relationshipId: string) => {
    setArchiveConfirm(null);
    try {
      setArchivingId(relationshipId);
      await TrainerMemberService.updateRelationshipStatus(relationshipId, 'inactive');
      await loadMembers({ silent: true });
    } catch (err: any) {
      setError(err.message || 'Failed to archive member');
    } finally {
      setArchivingId(null);
    }
  };

  const handleRestore = async (
    relationship: TrainerMemberRelationshipWithMember,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    const email = relationship.member?.email?.trim();
    try {
      setReactivatingId(relationship.id);
      await TrainerMemberService.reActivateConnection(relationship.id);

      if (email && shouldAttemptInviteEmail()) {
        try {
          await sendConnectionInvite({
            to: email,
            trainerName: trainerName || 'Your trainer',
            trainerId,
            relationshipId: relationship.id,
          });
          showToast('ok', `Connection restored — invitation sent to ${email}.`);
        } catch (emailErr: any) {
          console.warn('Restore invite email could not be sent:', emailErr);
          showToast(
            'error',
            emailErr?.message ||
              'Member restored to Pending, but the invitation email could not be sent.'
          );
        }
      } else if (!email) {
        showToast('ok', 'Connection restored — invite pending again (no email on file).');
      } else {
        showToast(
          'ok',
          'Connection restored — invite pending again (email sending is disabled in this environment).'
        );
      }

      await loadMembers({ silent: true });
    } catch (err: any) {
      setError(err.message || 'Failed to restore');
    } finally {
      setReactivatingId(null);
    }
  };

  const handleResendRelationship = async (
    relationship: TrainerMemberRelationshipWithMember,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    const email = relationship.member?.email?.trim();
    if (!email) {
      showToast('error', 'No email on file for this member.');
      return;
    }
    setResendingKey(`rel:${relationship.id}`);
    try {
      if (!shouldAttemptInviteEmail()) {
        throw new Error('Email sending is disabled (REACT_APP_SEND_INVITE_EMAIL=false).');
      }
      await sendConnectionInvite({
        to: email,
        trainerName: trainerName || 'Your trainer',
        trainerId,
        relationshipId: relationship.id,
      });
      showToast('ok', `Invitation resent to ${email}.`);
      await loadMembers({ silent: true });
    } catch (err: any) {
      showToast('error', err.message || 'Failed to resend');
    } finally {
      setResendingKey(null);
    }
  };

  const handleResendEmail = async (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setResendingKey(`pending:${email}`);
    try {
      if (!shouldAttemptInviteEmail()) {
        throw new Error('Email sending is disabled (REACT_APP_SEND_INVITE_EMAIL=false).');
      }
      await sendConnectionInvite({
        to: email,
        trainerName: trainerName || 'Your trainer',
        trainerId,
      });
      showToast('ok', `Invitation resent to ${email}.`);
      await loadMembers({ silent: true });
    } catch (err: any) {
      showToast('error', err.message || 'Failed to resend');
    } finally {
      setResendingKey(null);
    }
  };

  const handleInviteSuccess = useCallback(
    async (email: string) => {
      setPendingOpen(true);
      setFlashPendingKey(`email:${email.toLowerCase()}`);
      await loadMembers({ silent: true });
      window.setTimeout(() => setFlashPendingKey(null), 1800);
    },
    [loadMembers]
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'cadence' ? 'asc' : 'asc');
    }
  };

  const reloadMembersSilently = useCallback(() => loadMembers({ silent: true }), [loadMembers]);

  return (
    <div className="members-mgmt">
      <header className="page-head">
        <h1 className="page-title">
          Members<em>.</em>
        </h1>
        {(loading || rosterMode !== 'true-empty') && (
          <button type="button" className="btn-primary" onClick={() => addMemberModalOpen()}>
            <Plus width={14} height={14} aria-hidden />
            Invite member
          </button>
        )}
      </header>

      {error && <div className="mm-banner error">{error}</div>}
      {toast && <div className={`mm-banner ${toast.kind === 'ok' ? 'ok' : 'error'}`}>{toast.text}</div>}

      {loading ? (
        <div className="mm-empty">Loading members…</div>
      ) : rosterMode === 'true-empty' ? (
        <MembersEmptyHero onInvite={() => addMemberModalOpen()} />
      ) : (
        <>
          {rosterMode === 'active-empty' ? (
            <>
              <div className="action-row">
                <div className="section-title-inline">
                  <h2>In training</h2>
                  <span className="count-eyebrow">{pad2(0)} ACTIVE</span>
                </div>
              </div>
              <div className="active-empty-note" data-testid="members-active-empty-note">
                <p>
                  No one&apos;s training yet — <strong>{pendingRowsAll.length}</strong>{' '}
                  {pendingRowsAll.length === 1 ? 'invite' : 'invites'} still pending.
                </p>
                <button type="button" className="btn-ghost" onClick={viewPendingSection}>
                  View pending
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="toolbar">
                <div className="search">
                  <Search width={16} height={16} aria-hidden />
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search members"
                  />
                </div>
                <button
                  type="button"
                  className={`filter-chip${filterTrainingToday ? ' active' : ''}`}
                  title="Members with a session on today's date, whether logged already or still upcoming."
                  onClick={() => setFilterTrainingToday((v) => !v)}
                >
                  Training today <span className="count-pill tnum">{trainingTodayCount}</span>
                </button>
                <button
                  type="button"
                  className={`filter-chip${filterNeedsAttention ? ' active' : ''}`}
                  title="Flags a member when either is true: no completed session in 10+ days, or no upcoming session is on their plan."
                  onClick={() => setFilterNeedsAttention((v) => !v)}
                >
                  Needs attention <span className="count-pill tnum">{needsAttentionCount}</span>
                </button>
                <div className="results-meta">
                  <strong className="tnum">{pad2(activeFiltered.length)}</strong> active &middot;{' '}
                  <span className="tnum">{pad2(totalRoster)}</span> total
                </div>
              </div>

              <div className="active-table-wrap">
                {activeFiltered.length === 0 ? (
                  <div className="mm-empty">No members match these filters.</div>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="idx">№</th>
                        <th>
                          <button
                            type="button"
                            className={`th-inner sortable${sortKey === 'name' ? ' sort-active' : ''}`}
                            onClick={() => toggleSort('name')}
                          >
                            Name
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                transform:
                                  sortKey === 'name' && sortDir === 'asc' ? 'rotate(180deg)' : undefined,
                              }}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                        </th>
                        <th
                          className="col-strip"
                          title="8 bars = last 8 calendar weeks. Bar height shows that week's sessions as a % of this member's personal weekly target (25/50/75/100%+). Hover a bar for details."
                        >
                          Last 8 weeks
                        </th>
                        <th className="col-cadence">
                          <button
                            type="button"
                            className={`th-inner sortable${sortKey === 'cadence' ? ' sort-active' : ''}`}
                            onClick={() => toggleSort('cadence')}
                          >
                            Cadence
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                transform:
                                  sortKey === 'cadence' && sortDir === 'asc'
                                    ? 'rotate(180deg)'
                                    : undefined,
                              }}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                        </th>
                        <th className="col-last">Last Completed Session</th>
                        <th className="col-next">Next Scheduled Session</th>
                        <th className="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {activeFiltered.map((m, idx) => {
                        const name = displayNameFromProfile(m.member) || 'Member';
                        const email = m.member?.email || '';
                        const met = metricsByMemberId[m.memberId] ?? emptyMetrics();
                        const initials = initialsFromNameOrEmail(name, email);
                        return (
                          <tr
                            key={m.id}
                            data-training-today={met.trainingToday ? 'true' : 'false'}
                            data-attention={met.needsAttention ? 'true' : 'false'}
                            onClick={() => openProfile(m)}
                          >
                            <td className="idx tnum">{pad2(idx + 1)}</td>
                            <td>
                              <div className="row-member">
                                <div className="avatar green">{initials}</div>
                                <div className="meta">
                                  <div className="name-main">
                                    {name}
                                    {met.needsAttention && (
                                      <span
                                        className="attention-flag"
                                        title={met.needsAttentionTooltip ?? undefined}
                                      >
                                        Needs attention
                                      </span>
                                    )}
                                  </div>
                                  <div className="name-sub">{email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="col-strip">
                              <ConsistencyStrip strip={met.strip} />
                            </td>
                            <td className="col-cadence">
                              <span className="cadence" title={CADENCE_TOOLTIP}>
                                {formatCadenceDisplay(met.cadence)}
                                <small>×/wk</small>
                              </span>
                            </td>
                            <td className="col-last">
                              <span className={`date-cell tnum${met.last.warn ? ' warn' : ''}`}>
                                {met.last.label}
                              </span>
                            </td>
                            <td className="col-next">
                              <span
                                className={`date-cell${
                                  met.next.kind === 'empty'
                                    ? ' empty'
                                    : met.next.kind === 'today'
                                      ? ' today'
                                      : ' tnum'
                                }`}
                              >
                                {met.next.label}
                              </span>
                            </td>
                            <td className="actions">
                              <button
                                type="button"
                                className={`icon-btn${menu?.relationship.id === m.id ? ' menu-open' : ''}`}
                                aria-label={`Actions for ${name}`}
                                onClick={(e) => openRowMenu(e, m)}
                              >
                                <MoreVertical width={16} height={16} aria-hidden />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="strip-legend">
                <span>Consistency, last 8 weeks</span>
                <span className="mini-strip">
                  <span style={{ height: '25%', background: 'var(--mm-ink-faint)' }} />
                  <span style={{ height: '50%', background: 'var(--mm-ink-muted)' }} />
                  <span style={{ height: '75%', background: 'var(--mm-ink-soft)' }} />
                  <span style={{ height: '100%', background: 'var(--mm-accent)' }} />
                </span>
                <span className="key-low">low → high</span>
              </div>
            </>
          )}

          {/* Pending */}
          <section
            ref={pendingSectionRef}
            className={`collapsible${pendingOpen ? ' open' : ''}`}
          >
        <button type="button" className="collapsible-head" onClick={() => setPendingOpen((v) => !v)}>
          <div className="collapsible-head-left">
            <h2>Pending</h2>
            <span className="count-eyebrow">{pad2(pendingRowsAll.length)} INVITED</span>
          </div>
          <span className="collapsible-chev" aria-hidden>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
        <div className="collapsible-body">
          {pendingFiltered.length === 0 ? (
            <div className="mm-empty">No pending invites.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th className="idx">№</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Invited</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {pendingFiltered.map((row, idx) => {
                  if (row.kind === 'email') {
                    const stale = isInviteStale(row.createdAt, todayYmd);
                    const flash = flashPendingKey === `email:${row.email.toLowerCase()}`;
                    return (
                      <tr key={`email:${row.email}`} className={`pending-row${flash ? ' row-new-flash' : ''}`}>
                        <td className="idx tnum">{pad2(activeMembers.length + idx + 1)}</td>
                        <td>
                          <div className="row-member">
                            <div className="avatar dashed">{initialsFromNameOrEmail('', row.email)}</div>
                            <div className="meta">
                              <div className="name-main">
                                Invited member
                                {stale && (
                                  <span className="stale-flag" title={STALE_INVITE_TOOLTIP}>
                                    Stale
                                  </span>
                                )}
                              </div>
                              <div className="name-sub">{row.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge-status pending">
                            <span className="dot" />
                            Pending
                          </span>
                        </td>
                        <td>
                          <span className="date-cell tnum">{formatInviteAbsoluteDate(row.createdAt)}</span>
                        </td>
                        <td className="actions">
                          <span className="invite-actions">
                            <button
                              type="button"
                              className="btn-mini"
                              disabled={resendingKey === `pending:${row.email}`}
                              onClick={(e) => handleResendEmail(row.email, e)}
                            >
                              Resend
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  const r = row.relationship;
                  const name = displayNameFromProfile(r.member) || 'Invited member';
                  const email = r.member?.email || '';
                  const created = r.createdAt || r.requestedAt || '';
                  const stale = created ? isInviteStale(created, todayYmd) : false;
                  return (
                    <tr key={r.id} className="pending-row">
                      <td className="idx tnum">{pad2(activeMembers.length + idx + 1)}</td>
                      <td>
                        <div className="row-member">
                          <div className="avatar dashed">{initialsFromNameOrEmail(name, email)}</div>
                          <div className="meta">
                            <div className="name-main">
                              {name}
                              {stale && (
                                <span className="stale-flag" title={STALE_INVITE_TOOLTIP}>
                                  Stale
                                </span>
                              )}
                            </div>
                            <div className="name-sub">{email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge-status pending">
                          <span className="dot" />
                          Pending
                        </span>
                      </td>
                      <td>
                        <span className="date-cell tnum">
                          {created ? formatInviteAbsoluteDate(created) : '—'}
                        </span>
                      </td>
                      <td className="actions">
                        <span className="invite-actions">
                          <button
                            type="button"
                            className="btn-mini"
                            disabled={resendingKey === `rel:${r.id}`}
                            onClick={(e) => handleResendRelationship(r, e)}
                          >
                            Resend
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Archived */}
      <section className={`collapsible${archivedOpen ? ' open' : ''}`}>
        <button type="button" className="collapsible-head" onClick={() => setArchivedOpen((v) => !v)}>
          <div className="collapsible-head-left">
            <h2>Archived</h2>
            <span className="count-eyebrow">{pad2(archivedMembers.length)} ARCHIVED</span>
          </div>
          <span className="collapsible-chev" aria-hidden>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
        <div className="collapsible-body">
          {archivedFiltered.length === 0 ? (
            <div className="mm-empty">No archived members.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th className="idx">№</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {archivedFiltered.map((archived, idx) => {
                  const name = displayNameFromProfile(archived.member) || 'Member';
                  const email = archived.member?.email || '';
                  return (
                    <tr key={archived.id} className="archived-row">
                      <td className="idx tnum">{pad2(idx + 1)}</td>
                      <td>
                        <div className="row-member">
                          <div className="avatar muted">{initialsFromNameOrEmail(name, email)}</div>
                          <div className="meta">
                            <div className="name-main">{name}</div>
                            <div className="name-sub">{email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge-status archived">
                          <span className="dot" />
                          Archived
                        </span>
                      </td>
                      <td className="actions">
                        <span className="invite-actions">
                          <button
                            type="button"
                            className="btn-mini"
                            disabled={reactivatingId === archived.id}
                            onClick={(e) => {
                              void handleRestore(archived, e);
                            }}
                          >
                            Restore
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
        </>
      )}

      {menu &&
        createPortal(
          <div
            id="mm-row-menu"
            className="mm-row-menu"
            style={{ top: menu.top, left: menu.left }}
            role="menu"
          >
            <div
              className="menu-arrow"
              style={
                menu.arrowOnTop
                  ? { left: menu.arrowLeft, top: -6, bottom: 'auto', borderRadius: '3px 0 0 0' }
                  : { left: menu.arrowLeft, top: 'auto', bottom: -6, borderRadius: '0 3px 0 0' }
              }
            />
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                const rel = menu.relationship;
                setMenu(null);
                openProfile(rel);
              }}
            >
              View profile
            </button>
            <div className="menu-divider" />
            <button
              type="button"
              className="danger"
              role="menuitem"
              disabled={archivingId === menu.relationship.id}
              onClick={() => handleArchiveClick(menu.relationship)}
            >
              Archive member
            </button>
          </div>,
          document.body
        )}

      {archiveConfirm &&
        createPortal(
          <div
            className="mm-invite-overlay"
            role="presentation"
            onClick={() => setArchiveConfirm(null)}
          >
            <div
              className="mm-invite-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="archive-dialog-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="modal-close"
                onClick={() => setArchiveConfirm(null)}
                aria-label="Close"
              >
                <X width={16} height={16} aria-hidden />
              </button>
              <h2 id="archive-dialog-title" className="modal-title">
                Archive a member
              </h2>
              <p className="modal-sub">
                {archiveConfirm.activePlansCount > 0 ? (
                  <>
                    <strong>{archiveConfirm.memberName}</strong> has {archiveConfirm.activePlansCount}{' '}
                    active workout plan
                    {archiveConfirm.activePlansCount !== 1 ? 's' : ''} assigned by you. Archiving will
                    change their status to inactive.
                  </>
                ) : (
                  <>
                    Archive <strong>{archiveConfirm.memberName}</strong>? This will change their status
                    to inactive.
                  </>
                )}
              </p>
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setArchiveConfirm(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => doArchive(archiveConfirm.relationshipId)}
                >
                  Archive
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <AddMemberModalLayer
        trainerId={trainerId}
        trainerName={trainerName}
        onReloadMembers={reloadMembersSilently}
        onInviteSuccess={handleInviteSuccess}
      />
    </div>
  );
};
